import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { organizationState } from "redux/organizationSlice";
import { toggleCertificateFlag, selectIsCertificateFlagged } from "redux/flaggedItemsSlice";
import { loggedInUserState } from "redux/userSlice";
import { Link, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Button, Checkbox, Collapse, Box, Typography, Chip, CircularProgress, Skeleton, Badge, styled, Tooltip } from "@mui/material";
import { Menu, Popup } from "semantic-ui-react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMore from "@mui/icons-material/ExpandMore";
import SortableTHeadCell from "components/shared/SortableTHeadCell";
import StatusIndicator from "../shared/StatusIndicator";
import IconButton from "@mui/material/IconButton";
import IosShareIcon from "@mui/icons-material/IosShare";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import FlagIcon from "@mui/icons-material/Flag";
import OutlinedFlagIcon from "@mui/icons-material/OutlinedFlag";

import TableLoadingIndicator from "components/shared/TableLoadingIndicator";
import { awsDateToUS } from "utils/dateUtils";
import { findInStringsIgnoreCase, formatID } from "utils/stringUtils";
import ScanResultTableCell from "./ScanResultTableCell";
import { non_custom_insurance_types } from "constants/insurance_types";
import certificate_actions from "constants/certificate_actions";
import { getProjectLabel, getTableRowsPerPage, getVendorLabel } from "constants/organization_types";
import CertificatesActionModal from "./CertificatesActionModal";
import { vendorActions } from "redux/vendorSlice";
import { Certificatestatus } from "models";
import useCertificateActivities from "hooks/useCertificateActivities";
import Snackbar from "components/shared/Snackbar";
import { Certificatetype } from "models";
import FolderIcon from '@mui/icons-material/Folder';
import SupplementalDocuments from "./SupplementalDocuments";
import DocumentViewerModal from "components/shared/DocumentViewerModal";
import { parseSupplementalDocuments, getSupplementalDocumentUrls } from "utils/supplementalDocuments";

// Styled components for expand button with badge
const ExpandButtonWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "inline-flex",
}));

const DocumentCountBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    right: -25,
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: "0.65rem",
    minWidth: 16,
    height: 16,
    padding: "0 4px",
    backgroundColor: theme.palette.primary.main,
  },
}));

const columnConfig = {
  id: { label: "ID", noSort: true },
  insured: { label: "Insured" },
  vendor: { label: "Vendor", noSort: true },
  certificateType: { label: "Type", noSort: true },
  types: { label: "Coverage", noSort: true },
  subcontractorStatus: { label: "Subcontractor Status", noSort: true },
  producer: { label: "Producer" },
  status: { label: "COI Status", noSort: true, align: "center" },
  certificateDate: { label: "Certificate Date" },
  project: { label: "Project", noSort: true },
  lastModifiedDate: { label: "Last Modified" },
  createdDate: { label: "Created Date" },
  policyExp: { label: "Policy EXP" },
  sentTo: { label: "Sent To", noSort: true },
  sentBy: { label: "Sent By", noSort: true },
  read: { label: "Read", noSort: true },
  readBy: { label: "Read By", noSort: true },
  sentDate: { label: "Sent Date" },
  scan: { label: "Scan Status", noSort: true },
  actions: { label: "", noSort: true, align: "right" },
  selected: { label: "", noSort: true, align: "center" },
};

const defaultColumns = ["certificateType", "status", "insured", "certificateDate", "lastModifiedDate", "actions"];
const defaultColumnsForRequested = ["id", "certificateType", "status", "sentTo", "sentDate", "sentBy", "read"];

function getFirstExp(coverages) {
  if (coverages?.length) {
    return (
      coverages
        .map((coverage) => coverage.policyExp)
        .flat()
        .filter((exp) => exp)
        .sort((a, b) => {
          if (new Date(a) > new Date(b)) {
            return -1;
          } else {
            return 1;
          }
        })
        .pop() || null
    );
  }
  return null;
}

function COIItemRow(props) {
  const {
    id,
    insured,
    coverages,
    coverageType,
    certificateType,
    status,
    producer,
    sentTo,
    sentBy,
    readBy,
    onView,
    onSendEmail,
    certificateDate,
    firstExp,
    updatedAt: lastModifiedDate,
    createdAt: createdDate,
    columns = defaultColumns,
    projectID,
    projects = {},
    subcontractors = [],
    compact = false,
    selected = {},
    clientVendorID,
    onSelectionChange = () => {},
    hasMultipleSelectionPermissions,
    hideSelection,
    initialCoverageRequirements,
    style,
    supplementalCoiFiles,
    onViewDocument,
    onDownloadDocument
  } = props;

  const dispatch = useDispatch();
  const loggedInUser = useSelector(loggedInUserState);
  const isFlagged = useSelector(selectIsCertificateFlagged(id));
  const [open, setOpen] = useState(false);
  const [documentsWithUrls, setDocumentsWithUrls] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  const vendor = subcontractors.find((sub) => sub?.id === clientVendorID) || {};
  
  // Parse supplemental documents from JSON
  const supplementalDocs = useMemo(() => {
    // If supplementalCoiFiles is empty but files array has items,
    // convert files array to supplemental docs format
    if (!supplementalCoiFiles && props.files && props.files.length > 0) {
      const docsFromFiles = props.files.map((file, index) => ({
        id: `file-${index}`,
        filename: typeof file === 'string' ? file.split('/').pop() : file.name || 'Unknown',
        fileKey: typeof file === 'string' ? file : file.name,
        policyTypes: [],
        uploadedDate: new Date().toISOString().split('T')[0],
        fileSize: null
      }));
      return docsFromFiles;
    }
    
    const parsedDocs = parseSupplementalDocuments(supplementalCoiFiles);
    if (parsedDocs.length > 0) {
      console.log('COIItemRow has supplemental docs:', {
        certId: props.id,
        supplementalCoiFiles,
        parsedDocs
      });
    }
    return parsedDocs;
  }, [supplementalCoiFiles, props.files]);
  
  // Fetch document URLs when expanding
  useEffect(() => {
    if (open && supplementalDocs.length > 0 && documentsWithUrls.length === 0) {
      setLoadingDocuments(true);
      getSupplementalDocumentUrls(id, supplementalDocs)
        .then(docs => {
          setDocumentsWithUrls(docs);
          setLoadingDocuments(false);
        })
        .catch(err => {
          console.error('Error fetching document URLs:', err);
          setLoadingDocuments(false);
        });
    }
  }, [open, supplementalDocs, id, documentsWithUrls.length]);

  const getTypes = (coverages, coverageType) =>
    coverageType?.join(", ") || (coverages?.items?.filter((coverage) => !!coverage.policyNumber && (coverage.insuranceType || coverage.name)) || [])
      .map((coverage) => {
        if (non_custom_insurance_types.includes(coverage.insuranceType)) {
          return coverage.insuranceType;
        }
        return coverage.name ? String(coverage.name).substring(0, 3).toUpperCase() : coverage.insuranceType;
      })
      .join(", ");

  const [rowData, setRowData] = useState({});
  
  useEffect(() => {
    setRowData({
      insuredName: insured && insured.length > 2 ? insured.split("\n")?.[0] : "",
      producerName: producer && producer.length > 2 ? producer.split("\n")?.[0] : "",
      types: getTypes(coverages, coverageType),
      certificateDate: certificateDate !== "0000-01-01" ? certificateDate : null,
      lastModifiedDate: lastModifiedDate,
      firstExp: firstExp || null,
      createdAt: createdDate,
      ...(sentTo && { sentTo: [...sentTo].join(",") }),
      sentBy, sentBy,
      ...(readBy && { readBy: [...readBy].join(",") }),
    });
  }, [id]);

  const onUpdateScanStatus = useCallback((scanData) => {
    if (scanData && scanData.id === id) {
      setRowData((prevRowData) => {
        // Only update if values have actually changed
        const newInsuredName = prevRowData.insuredName || scanData?.insured?.split("\n")?.[0];
        const newProducerName = prevRowData.producerName || scanData?.producer?.split("\n")?.[0];
        const newTypes = prevRowData.types || getTypes(scanData.coverages);
        const newCertificateDate = prevRowData.certificateDate ?? scanData.certificateDate;
        const newLastModifiedDate = prevRowData.lastModifiedDate ?? scanData.updatedAt ?? new Date();
        const newFirstExp = prevRowData.firstExp ?? getFirstExp(scanData?.coverages?.items);
        
        // Check if anything actually changed
        if (
          prevRowData.insuredName === newInsuredName &&
          prevRowData.producerName === newProducerName &&
          prevRowData.types === newTypes &&
          prevRowData.certificateDate === newCertificateDate &&
          prevRowData.lastModifiedDate === newLastModifiedDate &&
          prevRowData.firstExp === newFirstExp
        ) {
          return prevRowData; // No changes, return same object to prevent re-render
        }
        
        return {
          ...prevRowData,
          insuredName: newInsuredName,
          producerName: newProducerName,
          types: newTypes,
          certificateDate: newCertificateDate,
          lastModifiedDate: newLastModifiedDate,
          firstExp: newFirstExp,
        };
      });
    }
  }, [id]);

  return !columns.includes("sentBy") ? (
    <>
      <TableRow style={{...style}}>
        {hasMultipleSelectionPermissions && columns.includes("selected") && (
          <TableCell padding="checkbox" align="center">
            {
              !hideSelection &&
              <Checkbox checked={!!selected?.[id]} onChange={(event) => onSelectionChange(id, event.target.checked)} />
            }
          </TableCell>
        )}
        {columns.includes("id") && (
          <TableCell align="center">
            <Popup
              hoverable={true}
              trigger={
                <Link
                  component={"button"}
                  onClick={() => onView(id)}
                  underline="none"
                  className="clickable"
                  color="inherit"
                  sx={{ textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "50px" }}
                >
                  {formatID(id)}
                </Link>
              }
            >
              {id}
            </Popup>
          </TableCell>
        )}
        {columns.includes("certificateType") && (
          <TableCell align="center">
            <StatusIndicator label={certificateType === Certificatetype.AGENT || certificateType === Certificatetype.MULTI_AGENT ? "Multi-Agent" : "Standard"} size="small" />
          </TableCell>
        )}
        {columns.includes("status") && (
          <TableCell align="center">
            <StatusIndicator label={status || Certificatestatus.REVIEW} size="small" />
          </TableCell>
        )}
        {columns.includes("insured") && (
          <TableCell align="center" onClick={() => onView(id)}>
            <Link component={"button"} underline="none" className="clickable" color="inherit" sx={{ textAlign: "center" }}>
              {rowData.insuredName}
            </Link>
          </TableCell>
        )}
        {columns.includes("vendor") && <TableCell align="center">{vendor?.vendor?.name}</TableCell>}
        {columns.includes("subcontractorStatus") && (
          <TableCell align="center">
            <StatusIndicator label={vendor?.status || "NO STATUS"} size="large" />
          </TableCell>
        )}
        {columns.includes("types") && <TableCell align="center">{rowData.types}</TableCell>}
        {columns.includes("producer") && (
          <TableCell align="center" sx={compact ? { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "60px" } : {}}>
            {compact ? (
              <Popup position="bottom left" trigger={<span>{rowData.producerName}</span>}>
                {rowData.producerName}
              </Popup>
            ) : (
              rowData.producerName
            )}
          </TableCell>
        )}
        {columns.includes("certificateDate") && <TableCell align="center">{rowData.certificateDate && awsDateToUS(rowData.certificateDate)}</TableCell>}
        {columns.includes("lastModifiedDate") && (
          <TableCell align="center">{rowData.lastModifiedDate && new Date(rowData.lastModifiedDate).toLocaleDateString("en-US")}</TableCell>
        )}
        {columns.includes("createdDate") && <TableCell align="center">{createdDate && new Date(createdDate).toLocaleDateString("en-US")}</TableCell>}
        {columns.includes("policyExp") && <TableCell align="center">{rowData.firstExp && awsDateToUS(rowData.firstExp)}</TableCell>}
        {columns.includes("project") && <TableCell align="center">{projects[projectID] || ""}</TableCell>}
        {columns.includes("scan") && (
          <TableCell align="center">
            <ScanResultTableCell id={id} onUpdateScanData={onUpdateScanStatus} />
          </TableCell>
        )}
        {columns.includes("actions") && (
          <TableCell align="right" sx={{ pr: 5 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
              <Tooltip title={isFlagged ? "Remove flag" : "Flag this certificate"}>
                <IconButton
                  onClick={() => {
                    const certificateData = {
                      id,
                      name: insured || 'Certificate',
                      insuredName: insured,
                      status,
                      certificateDate,
                      vendor: subcontractors.find((sub) => sub?.id === clientVendorID)?.vendor,
                      vendorName: subcontractors.find((sub) => sub?.id === clientVendorID)?.vendor?.name,
                      clientVendorID,
                      firstExp
                    };
                    dispatch(toggleCertificateFlag({
                      certificate: certificateData,
                      flaggedBy: loggedInUser?.email || loggedInUser?.id
                    }));
                  }}
                >
                  {isFlagged ? <FlagIcon color="error" /> : <OutlinedFlagIcon />}
                </IconButton>
              </Tooltip>
              <Popup
                position="bottom center"
                trigger={
                  <IconButton onClick={() => onSendEmail(id)}>
                    <IosShareIcon />
                  </IconButton>
                }
              >
                Share Certificate
              </Popup>
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => setOpen(!open)}
                sx={{
                  border: "1px solid",
                  borderColor: "grey.300",
                  borderRadius: "50%",
                  width: 32,
                  height: 32,
                  bgcolor: "background.paper",
                  "&:hover": {
                    bgcolor: "grey.100",
                  },
                }}
              >
                {supplementalDocs.length > 0 ? (
                  <DocumentCountBadge badgeContent={supplementalDocs.length} color="primary">
                    {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </DocumentCountBadge>
                ) : (
                  open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
                )}
              </IconButton>
            </Box>
          </TableCell>
        )}
      </TableRow>
      {open && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columns.length + (hasMultipleSelectionPermissions && columns.includes("selected") ? 1 : 0)}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <SupplementalDocuments
                documents={documentsWithUrls}
                onViewDocument={onViewDocument}
                loading={loadingDocuments}
              />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  ) : (
    <TableRow style={{...style}}>
      {columns.includes("id") && (
        <TableCell align="center">
          <Popup
            hoverable={true}
              trigger={
                <div>
                  {formatID(id)}
                </div>
            }
          >
            {id}
          </Popup>
        </TableCell>
        )}
      {columns.includes("certificateType") && (
        <TableCell align="center">
          <StatusIndicator label={certificateType === Certificatetype.AGENT || certificateType === Certificatetype.MULTI_AGENT ? "Multi-Agent" : "Standard"} size="small" />
        </TableCell>
      )}
      {columns.includes("status") && (
        <TableCell align="center">
          <StatusIndicator label={status} size="small" />
        </TableCell>
        )}
      {columns.includes("types") && <TableCell align="center">{rowData.types || JSON.parse(JSON.parse(initialCoverageRequirements || '{"coverages": "[]"}')?.coverages)?.map(c=>c.insuranceType || c.name)?.join(", ") }</TableCell>}
      {columns.includes("sentTo") && <TableCell align="center">{ rowData.sentTo}</TableCell>}
      {columns.includes("sentDate") && <TableCell align="center">{createdDate && new Date(createdDate).toLocaleDateString("en-US")}</TableCell>}
      {columns.includes("sentBy") && <TableCell align="center">{rowData.sentBy}</TableCell>}
      
      {columns.includes("read") && (
        <TableCell align="center">
          <StatusIndicator label={rowData.readBy?.length ? "YES": "NO"} color={rowData?.readBy?.length ? "success" : "error"} size="small" />
        </TableCell>
      )}

      {columns.includes("readBy") && <TableCell align="center">{rowData.readBy}</TableCell>}
      {columns.includes("policyExp") && <TableCell align="center">{rowData.firstExp && awsDateToUS(rowData.firstExp)}</TableCell>}
      {columns.includes("project") && <TableCell align="center">{projects[projectID] || ""}</TableCell>}

    </TableRow>
  );
}

// Multi-agent certificate row component
function MultiAgentRow ({ cert, columns, displayedColumns, coiActions, projects, subcontractors, compact, selectedCertificates }) {
  const [open, setOpen] = useState(true);
  const childCount = cert.childCertificates?.items?.length || 0;
  
  return (
    <>
      <TableRow sx={{ backgroundColor: "#fafafa" }}>
        <TableCell padding="checkbox">
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        <TableCell align="center">
          <Popup hoverable={true} trigger={<Typography variant="h6">{formatID(cert.id)}</Typography>}>
            {cert.id}
          </Popup>
        </TableCell>

        <TableCell align="center">
          <StatusIndicator label="MULTI AGENT" size="small" />
        </TableCell>

        <TableCell colSpan={displayedColumns.length - 2} align="left">
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Chip icon={<FolderIcon fontSize="small" />} label={`${childCount} ${childCount === 1 ? "Certificate" : "Certificates"}`} />
          </Box>
        </TableCell>
      </TableRow>

      {cert.childCertificates.items.map((childCert, index) => (
        <COIItemRow
          style={{ display: !open ? "none" : null, backgroundColor: "#fafafa" }}
          key={`child-cert-${index}`}
          {...childCert}
          coverages={childCert}
          {...coiActions}
          {...{ index, columns, compact }}
          projects={projects}
          subcontractors={subcontractors}
          selected={selectedCertificates}
          hideSelection
          hasMultipleSelectionPermissions
        />
      ))}
    </>
  );
}

export default function CertificateTable({
  certificates = [],
  paginatedCertificates = [],
  certificatesCount = 0,
  certificateStatusCounts = {},
  certificateStatusCountsLoading = false,
  onView = () => {},
  onSendEmail = () => {},
  columns = defaultColumns,
  compact = false,
  serverSide = false,
  onFetchData,
  projects = {},
  loading,
  defaultRows = 10,
  subcontractors = [],
  nextTokens,
  setNextTokens = () => {},
  hasMultipleSelectionPermissions = false,
}) {
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const dispatch = useDispatch();
  const { selectedClient } = useSelector(organizationState);
  const projectLabel = getProjectLabel(selectedClient);
  const vendorLabel = getVendorLabel(selectedClient);
  const tableRowsPerPage = getTableRowsPerPage(selectedClient);
  const [statusFilter, setStatusFilter] = useState(columns.includes("sentBy") ? Certificatestatus.REQUESTED : "ALL");
  const [searchPhrase, setSearchPhrase] = useState("");
  const [displayedColumns, setDisplayedColumns] = useState(columns);
  const [selectedCertificates, setSelectedCertificates] = useState({});
  const [isActionConfirmationModalOpen, setIsActionConfirmationModalOpen] = useState(false);
  const [onMultipleSelectedCertsActionLoader, setOnMultipleSelectedCertsActionLoader] = useState(false);
  const [, addCertificateActivity] = useCertificateActivities();
  const [message, setMessage] = useState();
  const [isStatusChanging, setIsStatusChanging] = useState(false);

  const handleViewDocument = (document) => {
    setSelectedDocument(document);
    setDocumentViewerOpen(true);
  };

  const handleDownloadDocument = async (document) => {
    // Implementation will be added for downloading documents
    console.log('Download document:', document);
    // TODO: Implement download functionality
  };

  const coiActions = {
    onView: (id) => onView(id),
    onSendEmail: (id) => onSendEmail(id, certificate_actions.SHARE_CERTIFICATE),
    onViewDocument: handleViewDocument,
    onDownloadDocument: handleDownloadDocument,
  };
  const handleChangeRowsPerPage = async (event) => {
    setTableConfig({
      ...tableConfig,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
    });
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setIsStatusChanging(true);

    
    if (status === Certificatestatus.REQUESTED) {
      setDisplayedColumns(defaultColumnsForRequested);
    } else {
      setDisplayedColumns(columns);
    }
    
    const newTableConfig = { ...tableConfig };
    if (status === "ALL" && newTableConfig.status) {
      delete newTableConfig.status;
      delete newTableConfig.statusFilter;
    }
    
    // Always reset tokens when changing status filter for DynamoDB
    setNextTokens([null]);
    
    setTableConfig({
      ...newTableConfig,
      page: 0,
      ...(status && status !== "ALL" && { status: status, statusFilter: "=" }),
    });
  };
  const [tableConfig, setTableConfig] = useState({
    sortBy: "lastModifiedDate",
    sortAsc: false,
    page: 0,
    rowsPerPage: tableRowsPerPage,
  });
  const { sortBy, sortAsc, page, rowsPerPage } = tableConfig;
  const setSort = (newSortBy, newSortAsc, pageNo) =>
    setTableConfig({
      ...tableConfig,
      sortBy: newSortBy,
      sortAsc: newSortAsc,
      ...(pageNo !== undefined && { page: pageNo }),
    });
  const setPage = (newPage) => setTableConfig({ ...tableConfig, page: newPage });
  const theadProps = { sortBy, sortAsc, setSort };
  const hasFilter = (!!searchPhrase && !!String(searchPhrase).trim());
  const hasStatusFilter = statusFilter && statusFilter !== "ALL";
  const fetchTimeoutRef = useRef(null);

  useEffect(() => {
    if (serverSide && onFetchData) {
      // Clear existing timeout
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      
      // For status changes, fetch immediately (no debounce)
      // For other changes (pagination, sorting), add small debounce
      const isStatusChange = tableConfig.status !== undefined || tableConfig.statusFilter !== undefined;
      const delay = isStatusChange ? 0 : 150;
      
      fetchTimeoutRef.current = setTimeout(() => {
        onFetchData({ ...tableConfig });
      }, delay);
    }
    
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [tableConfig]);
  
  // Clear status changing flag when loading changes
  useEffect(() => {
    if (!loading && isStatusChanging) {
      setIsStatusChanging(false);
    }
  }, [loading, isStatusChanging]);

  const { filteredCertificates, filteredCertificatesCount } = useMemo(() => {
    // For server-side pagination, always use paginated data when no search
    if (serverSide && !searchPhrase) {
      // Use the appropriate count based on current status filter
      let currentCount = certificatesCount || 0;
      if (certificateStatusCounts && Object.keys(certificateStatusCounts).length > 0) {
        if (statusFilter === "ALL") {
          currentCount = certificateStatusCounts.ALL || 0;
        } else if (statusFilter && certificateStatusCounts[statusFilter] !== undefined) {
          currentCount = certificateStatusCounts[statusFilter] || 0;
        }
      }
      
      return {
        filteredCertificates: paginatedCertificates || [],
        filteredCertificatesCount: currentCount
      };
    }
    
    // Client-side filtering (with or without search phrase)
    if (certificates?.length) {
      let toSort = [...certificates];
      
      console.log('CertificateTable filtering debug:', {
        originalCount: certificates.length,
        statusFilter,
        firstCertStatus: certificates[0]?.status,
        certificateStatuses: certificates.map(c => c.status),
        uniqueStatuses: [...new Set(certificates.map(c => c.status))]
      });
      
      // Apply status filter first (if not "ALL")
      if (statusFilter && statusFilter !== "ALL") {
        toSort = toSort.filter(certificate => certificate.status === statusFilter);
      } else if (statusFilter === "ALL") {
        toSort = toSort.filter(certificate => 
          certificate.status !== Certificatestatus.REQUESTED && 
          certificate.certificateType !== Certificatetype.MULTI_AGENT
        );
      }
      
      console.log('After status filter:', {
        filteredCount: toSort.length,
        statusFilter,
        searchPhrase
      });
      
      // Apply search filter
      if (searchPhrase) {
        toSort = toSort.filter(certificate => {
          // Get vendor/franchisee information
          const vendor = subcontractors.find((sub) => sub?.id === certificate.clientVendorID) || {};
          const vendorName = vendor?.vendor?.name || "";
          
          return findInStringsIgnoreCase(searchPhrase, [
            certificate.id,
            certificate.insured,
            certificate.coverages.items?.map((coverage) => coverage.type).join(""),
            certificate.producer,
            certificate.status,
            certificate.sentBy,
            certificate.sentTo?.join(""),
            awsDateToUS(certificate.certificateDate),
            displayedColumns.includes("project") ? projects[certificate.projectID] : null,
            new Date(certificate.updatedAt).toLocaleDateString("en-US"),
            vendorName, // Add vendor/franchisee name to search fields
          ]);
        });
      }
      
      console.log('After search filter:', {
        filteredCount: toSort.length,
        searchPhrase
      });
      
      // Sort
      toSort.sort((a, b) => {
        let larger;
        if (sortBy === "lastModifiedDate" && a.updatedAt && b.updatedAt) {
          larger = new Date(a.updatedAt).getTime() > new Date(b.updatedAt).getTime();
        } else if (sortBy === "createdDate" && a.createdAt && b.createdAt) {
          larger = new Date(a.createdAt).getTime() > new Date(b.createdAt).getTime();
        } else if (sortBy === "policyExp") {
          larger = new Date(a.firstExp) > new Date(b.firstExp);
        } else if (sortBy === "project") {
          larger = (projects[a.projectID] || "") > (projects[b.projectID] || "");
        } else {
          larger = (a[sortBy] || "") > (b[sortBy] || "");
        }
        if (larger) {
          return sortAsc ? 1 : -1;
        }
        return sortAsc ? -1 : 1;
      });
      
      return {
        filteredCertificatesCount: toSort.length,
        filteredCertificates: toSort.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
      };
    }
    
    // Default fallback - no certificates
    return {
      filteredCertificates: [],
      filteredCertificatesCount: 0
    };
  }, [certificates, paginatedCertificates, certificatesCount, certificateStatusCounts, statusFilter, sortBy, sortAsc, searchPhrase, projects, page, rowsPerPage, serverSide, displayedColumns, subcontractors]);
  
  const certsToDisplay = filteredCertificates;
  
  console.log('CertificateTable debug:', {
    certificates: certificates?.length,
    paginatedCertificates: paginatedCertificates?.length,
    filteredCertificates: filteredCertificates?.length,
    certsToDisplay: certsToDisplay?.length,
    loading,
    serverSide,
    searchPhrase,
    statusFilter,
    page,
    rowsPerPage
  });

  const onSelectedCertificatesChange = (id, value) => {
    if (value) {
      setSelectedCertificates((prev) => {
        const selectedVendor = certsToDisplay.find((vendor) => vendor?.id === id && vendor?.notificationStatus !== "DISABLED");
        if (selectedVendor) {
          prev[id] = selectedVendor;
        }
        return { ...prev };
      });
    } else {
      setSelectedCertificates((prev) => {
        delete prev[id];
        return { ...prev };
      });
    }
  };

  const onSelectedActionHandler = () => {
    setIsActionConfirmationModalOpen(true);
  };

  const onMultipleSelectedCertificatesActionHandler = (data) => {
    setOnMultipleSelectedCertsActionLoader(true);
    Object.entries(selectedCertificates).forEach(async ([key, value], index) => {
      const updateCertResult = await dispatch(
        vendorActions.createOrUpdateCertificate({
          certificateData: {
            ...value,
            status: data.status,
          },
        })
      );
      if (updateCertResult?.payload?.success) {
        addCertificateActivity({ ...value, status: data?.status }, [], { ...value });
        setMessage({
          severity: "success",
          text: `Successfully updated certificates`,
        });
      } else {
        setMessage({
          severity: "error",
          text: `Error on certificate update`,
        });
      }
    });
    setIsActionConfirmationModalOpen(false);
    setSelectedCertificates([]);
    setOnMultipleSelectedCertsActionLoader(false);
  };

  return (
    <>
      <TableContainer style={{ overflow: "visible" }} className="compact-table">
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow>
              <TableCell colSpan={3} style={{ border: "none" }}>
                <Menu text className="status-filter" style={{ alignItems: "center", marginLeft: 0, marginTop: 0 }}>
                  <Menu.Item active={statusFilter === "ALL"} onClick={() => handleStatusFilterChange("ALL")}>
                    All {certificateStatusCountsLoading ? <CircularProgress size={12} sx={{ ml: 0.5 }} /> : certificateStatusCounts.ALL !== undefined && `(${certificateStatusCounts.ALL})`}
                  </Menu.Item>
                  <Menu.Item active={statusFilter === Certificatestatus.APPROVED} onClick={() => handleStatusFilterChange(Certificatestatus.APPROVED)}>
                    Approved {certificateStatusCountsLoading ? <CircularProgress size={12} sx={{ ml: 0.5 }} /> : certificateStatusCounts.APPROVED !== undefined && `(${certificateStatusCounts.APPROVED})`}
                  </Menu.Item>
                  <Menu.Item active={statusFilter === Certificatestatus.INACTIVE} onClick={() => handleStatusFilterChange(Certificatestatus.INACTIVE)}>
                    Inactive {certificateStatusCountsLoading ? <CircularProgress size={12} sx={{ ml: 0.5 }} /> : certificateStatusCounts.INACTIVE !== undefined && `(${certificateStatusCounts.INACTIVE})`}
                  </Menu.Item>
                  <Menu.Item active={statusFilter === Certificatestatus.NOT_APPROVED} onClick={() => handleStatusFilterChange(Certificatestatus.NOT_APPROVED)}>
                    Not Approved {certificateStatusCountsLoading ? <CircularProgress size={12} sx={{ ml: 0.5 }} /> : certificateStatusCounts.NOT_APPROVED !== undefined && `(${certificateStatusCounts.NOT_APPROVED})`}
                  </Menu.Item>
                  <Menu.Item active={statusFilter === Certificatestatus.REVIEW} onClick={() => handleStatusFilterChange(Certificatestatus.REVIEW)}>
                    Review {certificateStatusCountsLoading ? <CircularProgress size={12} sx={{ ml: 0.5 }} /> : certificateStatusCounts.REVIEW !== undefined && `(${certificateStatusCounts.REVIEW})`}
                  </Menu.Item>
                  <Menu.Item active={statusFilter === Certificatestatus.EXPIRED} onClick={() => handleStatusFilterChange(Certificatestatus.EXPIRED)}>
                    Expired {certificateStatusCountsLoading ? <CircularProgress size={12} sx={{ ml: 0.5 }} /> : certificateStatusCounts.EXPIRED !== undefined && `(${certificateStatusCounts.EXPIRED})`}
                  </Menu.Item>
                  <Menu.Item active={statusFilter === Certificatestatus.REQUESTED} onClick={() => handleStatusFilterChange(Certificatestatus.REQUESTED)}>
                    Requested {certificateStatusCountsLoading ? <CircularProgress size={12} sx={{ ml: 0.5 }} /> : certificateStatusCounts.REQUESTED !== undefined && `(${certificateStatusCounts.REQUESTED})`}
                  </Menu.Item>
                </Menu>
              </TableCell>
              <TableCell colSpan={displayedColumns.length - 3} style={{ border: "none" }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search"
                  id="fullWidth"
                  value={searchPhrase}
                  onChange={(e) => setSearchPhrase(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "action.active", mr: 1, my: 0.5 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </TableCell>
            </TableRow>
          </TableHead>
        </Table>
        <Table>
          <TableHead>
            <TableRow>
              {!!displayedColumns?.length &&
                displayedColumns.map((column, index) => {
                  const config = columnConfig[column];

                  if (column === "project") {
                    config.label = projectLabel;
                  } else if (column === "subcontractorStatus") {
                    config.label = `${vendorLabel} Status`;
                  } else if (column === "vendor") {
                    config.label = vendorLabel;
                  }
                  const label = config?.label === "" ? "" : config?.label || column;
                  const align = config?.align || "center";

                  if (column === "selected") {
                    if (!hasMultipleSelectionPermissions) {
                      return null;
                    }
                    return (
                      <TableCell padding="checkbox" align={align} key={"cert" + index + "column"}>
                        <Button size="small" disabled={Object.keys(selectedCertificates).length < 1} onClick={onSelectedActionHandler}>
                          <ExpandMore />
                          Action
                        </Button>
                      </TableCell>
                    );
                  } else if (config?.noSort) {
                    return (
                      <TableCell 
                        align={align} 
                        key={"cert" + index + "column"}
                        sx={column === "actions" ? { pr: 5 } : {}}
                      >
                        {label}
                      </TableCell>
                    );
                  }
                  return (
                    <SortableTHeadCell
                      key={"cert" + index + "column"}
                      {...theadProps}
                      align={align}
                      label={label}
                      sortId={config?.sortId || column}
                      setNextTokens={setNextTokens}
                    />
                  );
                })}
            </TableRow>
          </TableHead>
          <TableBody>
            {(loading || isStatusChanging) && <TableLoadingIndicator colSpan={displayedColumns.length} />}
            {!loading && !isStatusChanging &&
              certsToDisplay?.map((cert, index) => {
                if (cert.certificateType === Certificatetype.MULTI_AGENT) {
                  return (
                    <MultiAgentRow
                      key={`multi-agent-${cert.id}`}
                      cert={cert}
                      columns={columns}
                      displayedColumns={displayedColumns}
                      coiActions={coiActions}
                      projects={projects}
                      subcontractors={subcontractors}
                      compact={compact}
                      selectedCertificates={selectedCertificates}
                    />
                  );
                } else {
                  return (
                    <COIItemRow
                      key={"cert" + index + "row"}
                      {...cert}
                      {...coiActions}
                      {...{ index, columns: displayedColumns, compact }}
                      projects={projects}
                      subcontractors={subcontractors}
                      selected={selectedCertificates}
                      onSelectionChange={onSelectedCertificatesChange}
                      hasMultipleSelectionPermissions={hasMultipleSelectionPermissions}
                    />
                  );
                }
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <DocumentViewerModal
        open={documentViewerOpen}
        document={selectedDocument}
        onClose={() => setDocumentViewerOpen(false)}
        onDownload={handleDownloadDocument}
      />
      <TablePagination
        nextIconButtonProps={{ style: { pointerEvents: serverSide && onFetchData && !hasFilter && page > 0 && !nextTokens[page + 1] ? "none" : null } }}
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={(certificateStatusCountsLoading && !searchPhrase) ? 0 : (filteredCertificatesCount || 0)}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelDisplayedRows={({ from, to, count }) => {
          // Only show skeleton loader if status counts are loading AND no search is active
          if (certificateStatusCountsLoading && !searchPhrase) {
            return (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Skeleton width={40} height={20} />
                <span>of</span>
                <Skeleton width={40} height={20} />
              </span>
            );
          }
          return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
        }}
      />
      <CertificatesActionModal
        isOpen={isActionConfirmationModalOpen}
        onCancel={setIsActionConfirmationModalOpen}
        selectedCertificates={selectedCertificates}
        loader={onMultipleSelectedCertsActionLoader}
        onUpdateHandler={onMultipleSelectedCertificatesActionHandler}
      />
      <Snackbar message={message} setMessage={setMessage} />
    </>
  );
}
